/** @file Finds room for one primitive: the current chunk and draw, or a fresh chunk and/or draw. */
import { RENDER_LAYOUT } from '@pose2d/geometry/renderBuffers';
import type { RenderBuffers } from '@pose2d/geometry/types';
import { invariant } from '@pose2d/std/invariant';
import type { Sink } from './createSink';

const VW = RENDER_LAYOUT.VERTEX_WORDS;
const DS = RENDER_LAYOUT.DRAW_STRIDE;

/** True when the last draw of `chunk` is not on `page` (overlay draws always use blend 0). */
function needsDraw(chunk: RenderBuffers, page: number): boolean {
  return chunk.drawCount === 0 || chunk.draws[(chunk.drawCount - 1) * DS] !== page;
}

/**
 * True when `chunk` can take `vertices` more vertices, `indices` more indices and, if needed, one more draw.
 * Vertex room is capped at `MAX_VERTICES` whatever the allocator returned, so Uint16 indices never wrap.
 */
function fits(chunk: RenderBuffers, page: number, vertices: number, indices: number): boolean {
  const drawOk = !needsDraw(chunk, page) || chunk.drawCount < chunk.draws.length / DS;
  const room = Math.min(chunk.vertexF32.length / VW, RENDER_LAYOUT.MAX_VERTICES);
  return chunk.vertexCount + vertices <= room && chunk.indexCount + indices <= chunk.indices.length && drawOk;
}

/** Moves to the next chunk (reusing `out[used]` or allocating), clears its counts and bounds. */
function nextChunk(sink: Sink): RenderBuffers {
  let chunk = sink.out[sink.used];
  if (chunk === undefined) {
    chunk = sink.allocate();
    sink.out.push(chunk);
  }
  sink.used += 1;
  chunk.vertexCount = 0;
  chunk.indexCount = 0;
  chunk.drawCount = 0;
  chunk.runCount = 0;
  chunk.bounds.fill(Infinity, 0, 2);
  chunk.bounds.fill(-Infinity, 2, 4);
  return chunk;
}

/** Returns a chunk whose last draw is `page` and which has room for the primitive; opens chunks/draws as needed. */
export function reserveRoom(sink: Sink, page: number, vertices: number, indices: number): RenderBuffers {
  let chunk = sink.used > 0 ? sink.out[sink.used - 1] : undefined;
  if (chunk === undefined || !fits(chunk, page, vertices, indices)) {
    chunk = nextChunk(sink);
    invariant(fits(chunk, page, vertices, indices), 'overlay chunk too small for one primitive (see createOverlayChunk minimums)');
  }
  if (needsDraw(chunk, page)) {
    const d = chunk.drawCount * DS, draws = chunk.draws;
    draws[d] = page;
    draws[d + 1] = 0;
    draws[d + 2] = chunk.indexCount;
    draws[d + 3] = 0;
    draws[d + 4] = chunk.vertexCount;
    draws[d + 5] = 0;
    chunk.drawCount += 1;
  }
  return chunk;
}
